/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE FULFILMENT VOCABULARY. [P4, relocated P6d]
 *
 *  A module of its own, with NO IMPORTS AT ALL, for one mechanical reason: the ORDER needs
 *  to report a derived fill state on its list row, and the FILL contract needs the order's
 *  kinds and its date type. Left in one file those two are a cycle, and a cycle whose first
 *  symptom is a `z.enum` reading `undefined` at module evaluation — which surfaces as a
 *  parse that accepts nothing, at start-up, with no line number worth having.
 *
 *  ⚠️ [amended P6d] IT LIVED IN `schema/` AND HAD TO MOVE, FOR `officerApp.constant.ts`'s
 *  REASON EXACTLY. P6d's operations list renders red/amber/green on a phone, so `pwa/` reads
 *  `FILL_STATES` at RUNTIME to iterate it — and §8 allows a runtime import from
 *  `@pams/shared/constants` and from nowhere else, because every other barrel drags zod into
 *  an app with a 150 KB budget. This file already had no imports, so the move is mechanical.
 *
 *  ⚠️ IT IS A MOVE, NOT A COPY. Values, names and reasoning are byte-identical and the four
 *  schema modules that used it now import from here. A second definition of the fulfilment
 *  vocabulary is how a colour and a stored rank come to disagree.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const ASSIGNMENT_STATUSES = ['ASSIGNED', 'SWAPPED_OUT', 'CANCELLED'] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];

/**
 * ⚠️ FR-5.3.1 FIXES THESE SYSTEM-WIDE: red = not named, amber = partial, green = named,
 * green + a lock = marked complete. Every surface uses the same four, and
 * `web/src/lib/labels.ts` is the only place they acquire a colour.
 */
export const FILL_STATES = ['NOT_NAMED', 'PARTIAL', 'NAMED', 'COMPLETE'] as const;
export type FillState = (typeof FILL_STATES)[number];

/**
 * ⚠️ A NUMBER, STORED AND INDEXED, BECAUSE "RED FIRST" IS A SORT.
 *
 * The inbox orders red → amber → green → complete across a PAGINATED list, so the ordering
 * has to be something Mongo can sort on with an index behind it (CLAUDE.md §7 requires one
 * for every `sortBy` value). A client-side sort would order one page and lie about the rest;
 * a computed `$sort` expression cannot be indexed. And it is a NUMBER rather than the label
 * because alphabetical order over `NAMED | NOT_NAMED | PARTIAL` is not red-amber-green —
 * and it reads correct in review, which is how that ships.
 */
export const FILL_RANK = { NOT_NAMED: 0, PARTIAL: 1, NAMED: 2, COMPLETE: 3 } as const satisfies Record<
  FillState,
  number
>;

const FILL_STATE_BY_RANK = Object.freeze(['NOT_NAMED', 'PARTIAL', 'NAMED', 'COMPLETE'] as const);

export const fillStateOfRank = (rank: number): FillState => FILL_STATE_BY_RANK[rank] ?? 'NOT_NAMED';

/**
 * The one derivation, so the server's stored rank and the screen's label cannot disagree.
 *
 * ⚠️ `COMPLETE` IS A DECISION SOMEBODY MADE, NOT AN ARITHMETIC RESULT — a division may
 * complete at partial with a shortfall reason (FR-5.3.6), and a fully named allocation that
 * nobody has marked is `NAMED`, not `COMPLETE`. Collapsing the two would make the lock mean
 * "the numbers add up", which is exactly what it does not mean.
 *
 * ⚠️ AND A REQUIREMENT OF ZERO IS `NOT_NAMED`, NEVER `NAMED`. Colouring a row green because
 * nobody was asked for anything says the work is done before it started — the argument
 * `forceGrid.ts`'s `balanceTone` already makes for its `'empty'` arm.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C5 §3] A COMPLETION IS TWO FACTS, AND UNTIL C5 THIS READ ONLY ONE OF THEM.
 *
 *  A client reported a live order reading `COMPLETE` with NOBODY named. Both reachable paths
 *  went through the line this comment used to sit above — `if (input.completed) return
 *  'COMPLETE'`, before any count was consulted:
 *
 *   • DECLARED. `completeShares` stamps `completed_at` AND `shortfall_reason` with no floor on
 *     `named_count`, so an order every division closed short at `0 of 12` was `COMPLETE`. That
 *     state is CORRECT — somebody decided it — and what was wrong was the WORD. `closedShort`
 *     is what lets a surface say `Closed short` instead, and it is a SECOND AXIS rather than a
 *     fifth `FILL_STATES` member: a member would move `allocation_fills.fill_rank`'s stored
 *     `{min: 0, max: 3}` and re-open every query that reads it. [LIFECYCLE]'s rule, for the
 *     fourth time after `archived_at`, `DUTY_EVENT_ATTESTATIONS` and `PostAudience`.
 *
 *   • STALE. `autoCompleteFullShares` stamps `completed_at` with NO reason the moment the last
 *     slot is named; `removeAssignment` then calls only `recountShares`, and nothing anywhere
 *     sets `completed_at` back to `null` ([C3c.2]). So removing every name left the share at
 *     rank 3 with `named_count: 0` and no reason to tell it apart from an honest full close.
 *     That state is WRONG, and the third clause below is what refuses it.
 *
 *  ⚠️ THE REPAIR IS ON THE READ, WHICH IS WHY THERE IS NO MIGRATION. `fill_rank` is a cache
 *  recomputed by `recountShares`; every surface derives the word from the live counts through
 *  this function, so a row already stored wrong reads correctly the next time anybody looks at
 *  it. A write-side fix would have had to reverse [C3c.2]'s "nothing sets it back to null" AND
 *  still leave the existing rows wrong.
 *
 *  ⚠️ `closedShort` IS OPTIONAL AND DEFAULTS TO `false`, AND THAT DEFAULT IS THE FIX RATHER
 *  THAN A CONVENIENCE. Every caller that predates C5 passes three keys and therefore asserts
 *  "no reason was recorded" — which is exactly the case the third clause must refuse. A
 *  required parameter would have been the safer-looking choice and would have made the repair
 *  opt-in at each of nine call sites.
 *
 *  ⚠️ [amended C5b §3/§8a] THIS IS STILL THE PER-SHARE RULE, AND TWO THINGS ABOVE STOPPED BEING
 *  TRUE. The repair on the read never reached the AGGREGATION readers: they asked `$ne:
 *  ['$shortfall_reason', null]`, which a MISSING field satisfies, so every never-short share
 *  counted as short (F1) and a stale lock still read COMPLETE on the inbox, the bell and the
 *  tiles. And `completed_at` IS set back to `null` now — `recountShares` clears an AUTOMATIC lock
 *  whose arithmetic fails ([C5b §8a]), reversing [C3c.2] for automatic locks only. A group of
 *  shares is answered by `fillOfTotals` below, never by summing and calling this.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const fillStateOf = (input: {
  namedCount: number;
  allocatedCount: number;
  completed: boolean;
  /** `allocation_fills.shortfall_reason` is set — the completion was DECLARED short. */
  closedShort?: boolean;
}): FillState => {
  /* A declared short close is complete BY DECISION, at any count. Its word is not "filled". */
  if (input.completed && input.closedShort === true) return 'COMPLETE';
  /* An automatic close is complete only while its own arithmetic still holds. */
  if (input.completed && input.allocatedCount > 0 && input.namedCount >= input.allocatedCount) return 'COMPLETE';
  if (input.allocatedCount <= 0 || input.namedCount <= 0) return 'NOT_NAMED';
  return input.namedCount >= input.allocatedCount ? 'NAMED' : 'PARTIAL';
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [UX1 §3c] WHO IS ASKING — the second axis `postFillStateOf` needs, and never a fifth
 *  `FILL_STATES` member.
 *
 *  A member would move `allocation_fills.fill_rank`'s stored `{min: 0, max: 3}` and re-open
 *  every query that reads it; [LIFECYCLE]'s rule is that a second AXIS leaves every existing
 *  status query working where a new status member forces all of them to be re-read. This is
 *  that rule applied for the third time, after `archived_at` and `DUTY_EVENT_ATTESTATIONS`.
 *
 *  ⚠️ THE NOUN IS `addressee`, WHICH `fillRef.util.ts` ALREADY OWNS. `addresseeOf` answers
 *  `unit ?? division` — the org unit a requirement row is ADDRESSED TO — and that is exactly
 *  the party for whom handing capacity down is a disposal of it. Naming the arm `division`
 *  would be wrong the day a unit-addressed row is read by the unit that holds it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const POST_AUDIENCES = ['addressee', 'oversight'] as const;

export type PostAudience = (typeof POST_AUDIENCES)[number];

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §1g] THE STATE OF A **POST**, WHICH IS NOT THE STATE OF ONE ALLOCATION.
 *
 *  ⚠️ DELEGATION IS NOT FULFILMENT, AND GREEN MEANS THE REQUIREMENT IS MET.
 *
 *  Confirmed with the client. Before C1f the fill screen had no post grain at all — it
 *  flattened every line into one row per allocation — so this question could not be asked.
 *  §1e introduces the row, and the row needs a colour: a division that has handed a post to
 *  four stations has DONE something, and has staffed nobody.
 *
 *  ⚠️ A GREEN ROW FOR AN UNSTAFFED DUTY IS THE SINGLE WORST OUTCOME AVAILABLE HERE, because
 *  it is the one that stops anyone chasing it. Amber says "moving, not finished"; red says
 *  "nobody has touched this". Both are true of different rows and the screen must tell them
 *  apart.
 *
 *  ⚠️ IT CALLS `fillStateOf` AND DOES NOT EDIT IT. That function has THREE expressions in
 *  this repository — itself, the stored `fill_rank`, and `fulfilmentInbox.service.ts`'s
 *  `$switch` — held together by a table-driven test. A fifth `FILL_STATES` member would move
 *  `allocation_fills.fill_rank`'s stored `{min: 0, max: 3}` and re-open every query that
 *  reads it; a fifth PARAMETER on `fillStateOf` would be one every existing caller has to
 *  pass `false` for, including an MQL transcription that cannot express it — [LIFECYCLE]'s
 *  one-vocabulary rule, and the forgotten one fails silently.
 *
 *  ⚠️ AND IT TAKES NO `completed`, DELIBERATELY. Completion is per ADDRESSEE per duty
 *  (FR-5.3.6b), so a post spanning two addressees where one has locked and one has not has no
 *  honest single lock. The lock stays where it already is — the share row and the footer's
 *  one pill. The cost, stated: a division that completes at PARTIAL with a shortfall reason
 *  leaves its post amber. That is exactly what the per-allocation dot does today.
 *
 *  ─── ⚠️ [amended UX1 §3] IT TAKES AN AUDIENCE NOW, AND THAT REVERSES THE RULING ABOVE FOR
 *      EXACTLY ONE READER ────────────────────────────────────────────────────────────────
 *
 *  [C1f §1g] settled, with the client, that a delegated-but-unnamed post stays AMBER. The
 *  client has since asked for GREEN. The two are reconcilable only by being precise about
 *  WHO IS ASKING, so this function now answers two questions rather than one:
 *
 *    `addressee`  — the org unit the post is ADDRESSED TO, reading its own worksheet.
 *                   "Have I done my part?" Handing capacity down to a station IS disposing
 *                   of that capacity, so a delegated slot is discharged.
 *    `oversight`  — anybody watching whether the duty gets STAFFED. "Is this covered?"
 *                   ⚠️ BYTE-IDENTICAL TO [C1f §1g], and the paragraph above still governs it.
 *
 *  ⚠️ THE WORST OUTCOME IS STILL THE WORST OUTCOME. §3b keeps the amber arm intact for every
 *  aggregate reader, and the server cannot even see delegation: `RANK_EXPRESSION` aggregates
 *  `allocation_fills`, which carries no `delegated_from_alloc_key` — that column lives on
 *  `arrangements.duty_points[].requirement_lines[].allocations[]`. So the inbox, both
 *  dashboard fill tiles and the notification bell are unreachable from here BY CONSTRUCTION,
 *  which is what makes exit criterion 9's second half a property rather than a promise.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const postFillStateOf = (input: {
  allocatedCount: number;
  /** Every name on the post, wherever it stands. Unchanged meaning. */
  namedCount: number;
  /** Slots sitting on a row this addressee handed down, named or not. */
  delegatedCount: number;
  /** How many of `namedCount` stand on one of those handed-down rows. */
  delegatedNamedCount: number;
  audience: PostAudience;
}): FillState => {
  /*
   * ⚠️ A NAME ON A HANDED-DOWN ROW IS ALREADY COVERED BY THAT ROW'S CAPACITY, AND ADDING THE
   * TWO COUNTS THE SAME SLOT TWICE.
   *
   * The obvious arithmetic is `namedCount + delegatedCount`, and it is WRONG in a reachable
   * case: 4 asked for, 2 handed to a station which names BOTH, 0 named on the 2 the division
   * kept. Naively 2 + 2 = 4 >= 4 → NAMED, GREEN — with two of the division's own slots empty.
   * That is §3b's catastrophe arriving on the division's own screen, from the one direction
   * nobody would look. Subtracting the overlap is the whole of the fix, and
   * `fillState.constant.test.ts` pins that exact case.
   *
   * A delegated row contributes its CAPACITY, a retained row contributes its NAMES, and no
   * slot is counted twice. `discharged <= allocatedCount` therefore holds by construction, so
   * `NAMED` fires only at exactly full and never by overshoot.
   */
  const discharged =
    input.audience === 'addressee'
      ? input.namedCount - input.delegatedNamedCount + input.delegatedCount
      : input.namedCount;

  /*
   * ⚠️ `allocatedCount > 0` GUARDS THE AMBER ARM. A post asking for nobody is `NOT_NAMED`
   * whatever has been done to it — `fillStateOf`'s own "a requirement of zero is NOT_NAMED,
   * never NAMED", which exists so a row cannot read as an achievement before it started.
   *
   * ⚠️ AND IT IS `oversight`-ONLY NOW. To the addressee a fully delegated post is discharged
   * and the line below answers NAMED; to everybody else this is [C1f §1g], unmoved.
   */
  if (input.audience === 'oversight' && input.allocatedCount > 0 && input.namedCount <= 0 && input.delegatedCount > 0)
    return 'PARTIAL';

  return fillStateOf({ namedCount: discharged, allocatedCount: input.allocatedCount, completed: false });
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5b §3] THE ONE RULE AT EVERY GRAIN.
 *
 *  A SHARE is SETTLED when it carries a completion and either somebody DECLARED it short or its
 *  own arithmetic still holds. A GROUP of shares — a division on a duty, a duty, an order — is
 *  COMPLETE iff EVERY share is settled, and closed short iff it is COMPLETE, one of its shares
 *  was declared, and the names are short. So a COMPLETE that is not closed short has
 *  `named >= allocated > 0`: `0 of 12 · All Filled` is a sentence this rule cannot produce, which
 *  `fillState.constant.test.ts` checks over every small world rather than over a list of shapes.
 *
 *  ⚠️ IT REPLACES "SUM THE COUNTS, THEN ASK WHETHER EVERY SHARE IS LOCKED". That was C5's shape
 *  at four readers, and it let one declared share make a sibling's STALE lock read COMPLETE (F4)
 *  — a question about locks answered for the group when it is a question about each share.
 *
 *  ⚠️ `api/src/utils/fillRank.util.ts` IS THE SAME RULE IN MQL, because the inbox FILTERS and
 *  SORTS on it before paginating and cannot compute it afterwards. The two are held together by
 *  `fillStateParity.test.ts`, which pins every reader against literal answers.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** One share's stored facts, as every reader of `allocation_fills` sees them. */
export type ShareFillFacts = {
  allocatedCount: number;
  namedCount: number;
  /** `completed_at` is set. */
  completed: boolean;
  /** `shortfall_reason` is a non-empty string — somebody DECLARED the share short. */
  declared: boolean;
};

/**
 * The share is done: a DECISION (declared, at any count) or a lock whose arithmetic still holds.
 *
 * ⚠️ IT IS `fillStateOf`'s OWN COMPLETE ARM, CALLED RATHER THAN RE-TYPED — so "settled" and the
 * per-share word cannot disagree.
 */
export const shareIsSettled = (facts: ShareFillFacts): boolean =>
  fillStateOf({
    namedCount: facts.namedCount,
    allocatedCount: facts.allocatedCount,
    completed: facts.completed,
    closedShort: facts.declared,
  }) === 'COMPLETE';

/**
 * ⚠️ [C5b §8a] THE ONE LOCK A RECOUNT MAY CLEAR — automatic, and no longer true.
 *
 * `autoCompleteFullShares` stamps a completion the instant a share's last slot is named, and
 * nobody decided it: it is a CACHE of "every slot is named". A DECLARED close is somebody's word
 * and is never touched, at any count.
 */
export const automaticLockIsStale = (facts: ShareFillFacts): boolean =>
  facts.completed && !facts.declared && !shareIsSettled(facts);

/** A group of shares, summed — the only input `fillOfTotals` needs. */
export type FillShareTotals = {
  shares: number;
  settled: number;
  /** Shares carrying a completion AND a reason — a declaration somebody finished. */
  declared: number;
  allocatedCount: number;
  namedCount: number;
};

export const fillTotalsOf = (shares: readonly ShareFillFacts[]): FillShareTotals =>
  shares.reduce<FillShareTotals>(
    (totals, facts) => ({
      shares: totals.shares + 1,
      settled: totals.settled + (shareIsSettled(facts) ? 1 : 0),
      declared: totals.declared + (facts.completed && facts.declared ? 1 : 0),
      allocatedCount: totals.allocatedCount + facts.allocatedCount,
      namedCount: totals.namedCount + facts.namedCount,
    }),
    { shares: 0, settled: 0, declared: 0, allocatedCount: 0, namedCount: 0 },
  );

/**
 * The group's state and whether it closed short.
 *
 * ⚠️ `shares > 0`, because an order nobody was asked for — or a draft — is not complete, and
 * `0 === 0` would say both were.
 *
 * ⚠️ `closedShort` REQUIRES THE NAMES TO BE SHORT. A division that closed short and then found
 * the people is COMPLETE and not short ([C5b] S09) — and the printed order already drops the note
 * for a share no longer short, so the word and the paper now agree.
 */
export const fillOfTotals = (totals: FillShareTotals): { fillState: FillState; closedShort: boolean } => {
  if (totals.shares > 0 && totals.settled === totals.shares) {
    const filled = totals.allocatedCount > 0 && totals.namedCount >= totals.allocatedCount;
    return { fillState: 'COMPLETE', closedShort: totals.declared > 0 && !filled };
  }
  return {
    fillState: fillStateOf({ namedCount: totals.namedCount, allocatedCount: totals.allocatedCount, completed: false }),
    closedShort: false,
  };
};

/**
 * ⚠️ [C5b §3] A FILL WORD IS A STATE PLUS ONE WORD FOR `COMPLETE`, AND `CLOSED_SHORT` IS NEVER A
 * `FILL_STATES` MEMBER. A member would move `allocation_fills.fill_rank`'s stored `{min: 0, max:
 * 3}` and re-open every query that reads it — [LIFECYCLE]'s rule. This is the vocabulary a
 * SURFACE words with; the state it words stays four-valued.
 */
export const FILL_WORDS = [...FILL_STATES, 'CLOSED_SHORT'] as const;
export type FillWord = (typeof FILL_WORDS)[number];

export const fillWordOf = (state: FillState, closedShort: boolean): FillWord =>
  state === 'COMPLETE' && closedShort ? 'CLOSED_SHORT' : state;

/**
 * ⚠️ [C5b §7] THE ONE RED: nobody named, AND something was asked.
 *
 * A zero ask is `NOT_NAMED` (`fillStateOf`'s rule) and is not a request nobody answered; a
 * declared close at nobody named is COMPLETE and IS an answer. Red on either would say the work
 * is undone when there is none to do, or when somebody already said why not.
 */
export const isUnanswered = (input: { fillState: FillState; allocatedCount: number }): boolean =>
  input.fillState === 'NOT_NAMED' && input.allocatedCount > 0;
